import { type HistoryMessageModel, WS_MSG_DATA_TYPE } from '@mindverse/accessor-open';
import classNames from 'classnames';
import dayjs from 'dayjs';
import dynamic from 'next/dynamic';
import type { CSSProperties } from 'react';
import { memo, useEffect, useMemo, useRef } from 'react';

import type { ReactClassNameInterface } from '@/types/common';
import type { ISourceCustomMessageAction, TNotice } from '@/types/wsNotice';
import { useCircleChatContext } from '@/ui/CircleChat/CircleChatContext';
import { useHomepageContext } from '@/ui/HomePage/HomepageContext';
import { EVENT } from '@/utils/event';
import { generateTimeString } from '@/utils/timestamp-format';

import type { SendMsgData, SessionAction } from '../../hooks/useChatSession';
import useMindStorage, { EStorageKey } from '../../hooks/useMindStorage';
import type { MessageModel } from '../../hooks/useSessionMsg';
import { WS_MSG_MULTIPLE_TEMPLATE_TYPE } from '../../interface/chat-content';
import SmallDotsLoading from '../SmallDotsLoading';
import CitationPopover from './CitationPopover';
import Message from './components/message';
import MindHint from './components/mindHint';
import ScrollContainer from './components/scrollContainer';
import SessionUserAction from './components/session-user-action';
import styles from './index.less';
import type MessageItem from './model/message-item';
import { EMessageAction, MessageItemType } from './model/message-item';
import useCitationPopover from './useCitationPopover';

const ChatText = dynamic(() => import('./components/markdown'), { ssr: false });

interface IProps extends ReactClassNameInterface {
  msgList: MessageModel[];
  initialNoMore?: boolean;
  loadingMsgId?: string;
  action?: SessionAction;
  inChatMode: boolean;
  inMobile: boolean;
  mindId: string;
  initMessage?: string;
  userId: number | string;
  inSharedHistory: boolean;
  needLogin?: boolean;
  onSend: (data: SendMsgData) => void;
  fetchHistory: () => Promise<HistoryMessageModel>;
  isCreatingSession: boolean;
}

function ChatList(props: IProps) {
  const {
    mindId,
    msgList,
    initialNoMore,
    loadingMsgId,
    inChatMode,
    inMobile,
    initMessage,
    userId,
    inSharedHistory = false,
    needLogin,
    action,
    fetchHistory,
    isCreatingSession
  } = props;

  const citationPopoverRef = useRef<HTMLDivElement>(null);
  const parentContainerRef = useRef<HTMLDivElement>(null);

  const [firstSendMsg] = useMindStorage(EStorageKey.FIRST_SEND_MSG);
  const [isIceBreaking] = useMindStorage(EStorageKey.ICE_BREAKING);
  const [sessionId] = useMindStorage(EStorageKey.SESSION_ID);

  const [showMessageLoading] = useMindStorage(EStorageKey.SHOW_MESSAGE_LOADING);

  const { hoveredElement: citationHoverElement, onMouseMove: onCitationHoverMoved } =
    useCitationPopover({
      parentContainerElement: parentContainerRef.current,
      popoverElement: citationPopoverRef.current
    });

  const onlyOneMsg = useMemo(() => {
    return msgList.length === 1;
  }, [msgList.length]);

  const { homepageData } = useHomepageContext();

  const { circleDetail } = useCircleChatContext();

  useEffect(() => {
    const handleWsNotice = (event: Event) => {
      const e = event as CustomEvent;
      const data: TNotice = e.detail as TNotice;

      if (data.sourceType !== 'messageAction') {
        return;
      }

      const sourceCustom = data.sourceCustom as ISourceCustomMessageAction;

      if (sourceCustom.sessionId !== sessionId) {
        return;
      }

      const actionType = data.sourceAction;

      if (actionType !== 'revoke' && actionType !== 'shield') {
        return;
      }

      const targetMessageId = sourceCustom.messageId || data.sourceId;

      if (!targetMessageId) {
        return;
      }

      dispatchEvent(
        new CustomEvent(EVENT.MESSAGE_RECALLED, {
          detail: {
            messageId: targetMessageId,
            sendUserId: sourceCustom.sendUserId,
            sender: sourceCustom.sender,
            actionStatus: actionType === 'shield' ? EMessageAction.SHIELDED : EMessageAction.REVOKED
          }
        })
      );
    };

    addEventListener(EVENT.WS_NOTICE, handleWsNotice);

    return () => {
      removeEventListener(EVENT.WS_NOTICE, handleWsNotice);
    };
  }, [sessionId]);

  function filterMsgItem(msgItem: MessageModel): boolean {
    if (
      msgItem.list[0].sender === MessageItemType.umm &&
      msgItem.list.length === 1 &&
      msgItem.list[0].multipleData &&
      msgItem.list[0].multipleData[0]?.singleDataType === WS_MSG_DATA_TYPE.text &&
      msgItem.list[0].multipleData[0]?.modal.answer === ''
    ) {
      return false;
    }

    // 特定 Template 过滤
    if (msgItem.list[0].sender === MessageItemType.umm) {
      const foundIndex = msgItem.list.findIndex((item) => {
        const template = item.multipleData ? item.multipleData[0]?.modal.template : '';

        if (template) {
          try {
            const templateName = JSON.parse(template).templateName;

            if (templateName === WS_MSG_MULTIPLE_TEMPLATE_TYPE.MebotChatOver) {
              return true;
            }
          } catch (_e) {}
        }

        return false;
      });

      if (foundIndex > -1) {
        return false;
      }
    }

    return true;
  }

  const validTimelist = useMemo(() => {
    const retArr: MessageModel[] = [];
    let lastDisplayedTimestamp: number = 0;

    msgList.forEach((msgItem) => {
      if (!filterMsgItem(msgItem)) {
        return;
      }

      const timestamp = (msgItem.list[0] as MessageItem).timestamp;

      if (timestamp) {
        if (
          !lastDisplayedTimestamp ||
          !dayjs(lastDisplayedTimestamp).isSame(dayjs(timestamp), 'day') ||
          timestamp - lastDisplayedTimestamp >= 300000
        ) {
          lastDisplayedTimestamp = timestamp;
          msgItem.timestamp = timestamp;
        }
      }

      retArr.push(msgItem);
    });

    return retArr;
  }, [msgList]);

  const renderMsgList = useMemo(() => {
    return validTimelist.map((msg: MessageModel, index: number) => {
      const isLast = index === validTimelist.length - 1;
      const isLoading =
        (msg.messageId === loadingMsgId && loadingMsgId !== undefined) ||
        (loadingMsgId === '' && isLast);

      return (
        <>
          {msg.timestamp && (
            <div className="text-center text-xs text-gray-400 my-4">
              {generateTimeString(msg.timestamp)}
            </div>
          )}
          <Message
            key={
              msg.messageId +
              msg.type +
              (msg.type === MessageItemType.system
                ? (msg.list[0] as MessageItem).dividerContent
                : '')
            }
            isLast={isLast}
            isLoading={isLoading}
            messageModel={msg}
            showActions={isLast && !inSharedHistory && !onlyOneMsg}
          />
        </>
      );
    });
  }, [validTimelist, loadingMsgId, inSharedHistory, onlyOneMsg]);
  const validMsgList = useMemo(() => renderMsgList.filter((item) => !!item), [renderMsgList]);

  const showSessionLoading = useMemo(
    () => isCreatingSession || (!isCreatingSession && !validMsgList.length && !!firstSendMsg),
    [isCreatingSession, validMsgList, firstSendMsg]
  );
  const userSendMsgList = useMemo(
    () => msgList.filter((item) => item.type === MessageItemType.client),
    [msgList]
  );

  const citationHoveredStyle: CSSProperties | undefined = useMemo(() => {
    return citationHoverElement
      ? {
          left: citationHoverElement.x,
          top: citationHoverElement.y,
          opacity: 1,
          display: 'block',
          maxWidth: citationHoverElement.maxWidth,
          minWidth: citationHoverElement.minWidth
        }
      : undefined;
  }, [citationHoverElement]);

  // 改成只有自己的消息发送出去的时候有taptap
  const isSelfMessageLoading = useMemo(
    () =>
      (msgList.at(-1)?.type === MessageItemType.client && msgList.at(-1)?.sendUserId == userId) ||
      isIceBreaking,
    [msgList, userId, isIceBreaking]
  );

  // TODO userId 用户未登录，提供假的消息
  if (initMessage && !userId) {
    return (
      <ScrollContainer
        key="init"
        bigPadding={!!inChatMode && !inMobile}
        fetchHistory={fetchHistory}
        initialNoMore={initialNoMore}
        mindId={mindId}
        msgListLength={msgList.length}
        needLogin={needLogin}
      >
        <div style={{ position: 'relative' }}>
          <div className={styles.msgContainer}>
            <div className={styles.receiveContainer}>
              <ChatText
                className={styles.receiveTextMd}
                content={initMessage}
                messageId="initMessage"
              />
            </div>
          </div>
        </div>
      </ScrollContainer>
    );
  }

  return (
    <div className="w-full h-full relative">
      <ScrollContainer
        ref={parentContainerRef}
        bigPadding={!!inChatMode && !inMobile}
        fetchHistory={fetchHistory}
        inMobile={inMobile}
        initialNoMore={initialNoMore}
        loadingMsgId={loadingMsgId}
        mindId={mindId}
        msgListLength={msgList.length}
        needLogin={needLogin}
        onMouseMove={(e) => {
          onCitationHoverMoved?.(e);
        }}
        userSendMsgListLength={userSendMsgList.length}
      >
        {showSessionLoading ? <SmallDotsLoading /> : null}
        {renderMsgList}

        <div className="h-[1px]" id="chat-last-level" />
        {/* Homepage别人关闭了自动回复，就不出taptap了 */}
        {(!!homepageData && homepageData.autoReply == false) ||
        !showMessageLoading ||
        circleDetail ? null : (
          <MindHint
            className={classNames(
              isSelfMessageLoading ? 'opacity-100' : 'opacity-0 translate-y-2',
              'transition-all first:mt-4'
            )}
          />
        )}

        {action && <SessionUserAction action={action} className="mb-[--cc-last-msg-margin]" />}
      </ScrollContainer>
      <CitationPopover
        ref={citationPopoverRef}
        className="absolute top-0 left-0 opacity-0 hidden"
        detail={citationHoverElement?.detail ?? '{}'}
        linkInfo={citationHoverElement?.linkInfo}
        style={citationHoveredStyle}
        url={citationHoverElement?.url ?? ''}
      />
    </div>
  );
}

export default memo(ChatList);
